import { useL10n } from "@ews/react-localization-context";
import { IonInput, IonItem, IonLabel } from "@ionic/react";
import React, { useRef } from "react";
import { useIsMobile } from "../../../Responsive";
import globalStyle from '../GlobalSettings.module.scss';
import style from './SettingsDateInput.module.scss';
import { InputChangeEventDetail, InputInputEventDetail } from "@ionic/core";
import { SettingsDateInputProps } from "./types";

const SettingsDateInput: React.FC<SettingsDateInputProps> = ({
    endText = '',
    startText = '',
    onChange,
    value,
    inputType = "date",
    hidden = false,
    labelPosition = 'stacked',
    text = '',
    tooltip,
    disabled,
    hasFocus,
    required,
    lines = undefined,
    debounce,
    onFocus,
    onBlur,
    id,
    inputRef,
    min,
    max,
    step
}) =>
{
    const { translate: t } = useL10n();
    const mobileView = useIsMobile();
    const inputStyle = mobileView ? 'mobileInput' : 'webInput';
    const innerInputRef = useRef<HTMLIonInputElement>(null);

    const normalizedDate = value
        ? value instanceof Date
            ? value
            : new Date(value)
        : undefined;


    const inputValue = normalizedDate
        ? normalizedDate.toISOString().slice(0, 10)
        : "";

    const handleFocusAfterReload = (ref: React.RefObject<HTMLIonInputElement>) =>
    {
        ref.current?.setFocus();
    };

    const _hasFocus = useRef<boolean>(false);

    const handleFocusOut = () =>
    {
        _hasFocus.current = false;
    };
    const handleFocusIn = () =>
    {
        _hasFocus.current = true;
    };

    const handleChange = (detail: InputInputEventDetail | InputChangeEventDetail) =>
    {
        const val = detail.value as string;
        if (!val) {
            if (onChange) onChange(undefined as any);
            return;
        }
        const dateValue = new Date(val);
        if (onChange) onChange(dateValue);
    };
    const handleBlur = (e: CustomEvent) =>
    {
        handleFocusOut();
        const rawValue = (inputRef?.current ?? innerInputRef.current)?.value;
        if (!rawValue) return;
        const dateValue = new Date(rawValue as string);
        if (onBlur) onBlur({ value: dateValue, focus: _hasFocus.current, event: e });
    };

    if (hasFocus) handleFocusAfterReload(inputRef || innerInputRef);

    return (
        <IonItem hidden={hidden} lines={lines} className={`${style.overflow} ${globalStyle.setting} ${hasFocus ? style.hasFocus : ''}`}>
            <div className={style.container} style={{ width: "100%" }}>
                <div className={style.containerInput}>
                    <span className={`${style.flex} ${style.justifyStart}`}>
                        <IonLabel
                            style={{ whiteSpace: "break-spaces" }}
                            position={labelPosition}>
                            {`${startText}${t(text)}${endText}`}{required ? <span className={`${globalStyle.required}`}>*</span> : null}
                        </IonLabel>
                        <span className={style.tooltip}>
                            {mobileView ? null : tooltip}
                        </span>
                    </span>
                    <IonInput
                        ref={inputRef || innerInputRef}
                        label=""
                        id={id}
                        max={max}
                        min={min}
                        step={step}
                        debounce={debounce}
                        onIonFocus={() =>
                        {
                            handleFocusIn();
                            if (onFocus) onFocus();
                        }}
                        onIonBlur={handleBlur}
                        required={required}
                        disabled={disabled}
                        value={inputValue}
                        type={inputType}
                        className={`${style.inputAlign} ${globalStyle[inputStyle]} ${style.noArrows}`}
                        onIonInput={({ detail }) =>
                        {
                            if (debounce) handleChange(detail);
                        }}
                        onIonChange={({ detail }) =>
                        {
                            if (!debounce) handleChange(detail);
                        }}
                    />
                </div>
            </div>
        </IonItem>
    );
};

export default SettingsDateInput;
